import React, { useState, useEffect } from 'react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { AlertCircle, CheckCircle, Clock, GitBranch, Server, Database, Eye, Copy, Check } from 'lucide-react';
import { Spinner } from '../../design-system';

interface ServiceStatus {
  status: 'up' | 'down';
  message: string;
}

interface HealthResponse {
  status: string;
  environment: string;
  services: {
    database: ServiceStatus;
    cache: ServiceStatus;
    observability: ServiceStatus;
  };
  tz: string;
  timestamp: string;
  version: string;
}

const SystemHealthSection: React.FC = () => {
  const api = useAuthenticatedApi();
  const [healthData, setHealthData] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedVersion, setCopiedVersion] = useState(false);

  console.log('SystemHealthSection rendering, loading:', loading, 'error:', error, 'healthData:', healthData);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get<HealthResponse>('/health');
        console.log('Health data received:', response);
        setHealthData(response);
        setError(null);
      } catch (err) {
        setError('Failed to fetch health data');
        console.error('Health check error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [api]);

  const fetchHealthData = async () => {
    try {
      setLoading(true);
      const response = await api.get<HealthResponse>('/health');
      console.log('Health data received:', response);
      setHealthData(response);
      setError(null);
    } catch (err) {
      setError('Failed to fetch health data');
      console.error('Health check error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'up':
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'down':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'up':
      case 'healthy':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'down':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const handleCopyVersion = async () => {
    const version = healthData?.version || 'unknown';
    try {
      await navigator.clipboard.writeText(version);
      setCopiedVersion(true);
      setTimeout(() => setCopiedVersion(false), 2000);
    } catch (err) {
      console.error('Failed to copy version:', err);
    }
  };

  if (loading && !healthData) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center">
          <Spinner size={32} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center text-red-600 dark:text-red-400">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-ui-text-primary flex items-center">
          System Health
        </h2>
        <button
          onClick={fetchHealthData}
          className="text-sm text-ui-text-secondary hover:text-ui-text-primary"
        >
          Refresh
        </button>
      </div>

      {healthData && (
        <div className="space-y-6">
          {/* Overall Status */}
          <div className="flex items-center justify-between p-4 bg-ui-background-secondary rounded-lg">
            <div className="flex items-center">
              {getStatusIcon(healthData.status)}
              <span className="ml-2 font-medium text-ui-text-primary">Overall Status</span>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(healthData.status)}`}>
              {healthData.status.charAt(0).toUpperCase() + healthData.status.slice(1)}
            </span>
          </div>

          {/* System Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-ui-background-secondary rounded-lg">
              <div className="flex items-center mb-2">
                <Server className="w-4 h-4 text-ui-text-secondary mr-2" />
                <span className="text-sm font-medium text-ui-text-secondary">Environment</span>
              </div>
              <span className="text-lg text-ui-text-primary">{healthData.environment}</span>
            </div>

            <div className="p-4 bg-ui-background-secondary rounded-lg">
              <div className="flex items-center mb-2">
                <GitBranch className="w-4 h-4 text-ui-text-secondary mr-2" />
                <span className="text-sm font-medium text-ui-text-secondary">Git Commit</span>
              </div>
              <button
                onClick={handleCopyVersion}
                className="flex items-center gap-2 w-full text-ui-text-primary font-mono hover:text-brand-primary dark:hover:text-brand-primary transition-colors"
                title={healthData?.version || 'unknown'}
              >
                <span className="text-sm truncate flex-1 text-left">
                  {(healthData?.version || 'unknown').substring(0, 8)}
                </span>
                {copiedVersion ? (
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                ) : (
                  <Copy className="w-4 h-4 opacity-50 hover:opacity-100 flex-shrink-0" />
                )}
              </button>
            </div>

            <div className="p-4 bg-ui-background-secondary rounded-lg">
              <div className="flex items-center mb-2">
                <Clock className="w-4 h-4 text-ui-text-secondary mr-2" />
                <span className="text-sm font-medium text-ui-text-secondary">Timezone</span>
              </div>
              <span className="text-lg text-ui-text-primary">{healthData.tz}</span>
            </div>

            <div className="p-4 bg-ui-background-secondary rounded-lg">
              <div className="flex items-center mb-2">
                <Clock className="w-4 h-4 text-ui-text-secondary mr-2" />
                <span className="text-sm font-medium text-ui-text-secondary">Last Check</span>
              </div>
              <span className="text-sm text-ui-text-primary">{formatTimestamp(healthData.timestamp)}</span>
            </div>
          </div>

          {/* Services Status */}
          <div>
            <h3 className="text-lg font-medium text-ui-text-primary mb-4">Services</h3>
            <div className="space-y-3">
              {/* Database */}
              <div className="flex items-center justify-between p-4 bg-ui-background-secondary rounded-lg">
                <div className="flex items-center">
                  <Database className="w-5 h-5 text-ui-text-secondary mr-3" />
                  <div>
                    <div className="font-medium text-ui-text-primary">Database</div>
                    <div className="text-sm text-ui-text-secondary">
                      {healthData.services.database.message}
                    </div>
                  </div>
                </div>
                {getStatusIcon(healthData.services.database.status)}
              </div>

              {/* Cache */}
              <div className="flex items-center justify-between p-4 bg-ui-background-secondary rounded-lg">
                <div className="flex items-center">
                  <Server className="w-5 h-5 text-ui-text-secondary mr-3" />
                  <div>
                    <div className="font-medium text-ui-text-primary">Cache</div>
                    <div className="text-sm text-ui-text-secondary">
                      {healthData.services.cache.message}
                    </div>
                  </div>
                </div>
                {getStatusIcon(healthData.services.cache.status)}
              </div>

              {/* Observability */}
              <div className="flex items-center justify-between p-4 bg-ui-background-secondary rounded-lg">
                <div className="flex items-center">
                  <Eye className="w-5 h-5 text-ui-text-secondary mr-3" />
                  <div>
                    <div className="font-medium text-ui-text-primary">Observability</div>
                    <div className="text-sm text-ui-text-secondary">
                      {healthData.services.observability.message}
                    </div>
                  </div>
                </div>
                {getStatusIcon(healthData.services.observability.status)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemHealthSection;